import type {ClaveDeInforme} from '@cnc/contracts';
import {useState} from 'react';
import {Link} from 'react-router';
import {ErrorDeApi} from '../api/cliente';
import {
  RUTA_DEL_CONSOLIDADO_DE_PROVISIONES,
  rutaDeInformeGenerado,
  useEjecutarInforme,
  useInformes,
  useProvisiones,
} from '../api/documentos';
import {AYUDA_INFORMES as A} from '../componentes/ayuda-general';
import {NotaDeAyuda} from '../componentes/nota-de-ayuda';
import {PestanasAnalisis} from '../componentes/pestanas-analisis';
import {TablaDeProvisiones} from '../componentes/provisiones-de-exportacion';
import {tr} from '../i18n/idioma';
import {fechaHora, numero} from '../utilidades/formato';

const boton =
  'rounded-md bg-verde px-3 py-1.5 text-sm font-medium text-white hover:bg-verde-oscuro disabled:opacity-50';

// Catalogo de informes generales: se generan en el servidor y quedan en el
// historial con su archivo. Cualquier rol puede generarlos.
export function PaginaInformes() {
  const informes = useInformes();
  const ejecutar = useEjecutarInforme();
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [enCurso, setEnCurso] = useState<ClaveDeInforme | null>(null);
  const [ultimo, setUltimo] = useState<number | null>(null);
  // Por defecto los informes salen como los archivos vigentes; con la casilla
  // incluyen el historial cargado de consolidados viejos (2020-2023).
  const [conHistorial, setConHistorial] = useState<Partial<Record<ClaveDeInforme, boolean>>>({});

  const generar = async (clave: ClaveDeInforme) => {
    setMensaje(null);
    setEnCurso(clave);
    try {
      const e = await ejecutar.mutateAsync({clave, incluirHistorial: conHistorial[clave] === true});
      setUltimo(e.id);
    } catch (error) {
      setMensaje(
        error instanceof ErrorDeApi
          ? error.message
          : tr('No se pudo generar el informe.', 'Could not generate the report.'),
      );
    } finally {
      setEnCurso(null);
    }
  };

  return (
    <section className="max-w-6xl space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-verde-oscuro">{tr('Informes', 'Reports')}</h1>
        <p className="text-sm text-apagado">
          {tr(
            'Los libros de Excel que antes se regeneraban desde la app original, ahora generados en el servidor y guardados en el historial. Los reportes del inventario (tablas dinámicas y gráficas) están en',
            'The Excel workbooks that used to be regenerated from the original app, now generated on the server and saved in the history. The inventory reports (pivot tables and charts) are in',
          )}{' '}
          <Link to="/analisis/reportes" className="text-verde hover:underline">
            {tr('Análisis → Reportes', 'Analysis → Pivot reports')}
          </Link>
          .
        </p>
      </div>
      <PestanasAnalisis />
      {informes.isError && <p className="text-sm text-alerta">{informes.error.message}</p>}
      {mensaje && <p className="text-sm text-alerta">{mensaje}</p>}

      {informes.data && (
        <div className="grid gap-4 md:grid-cols-2">
          {informes.data.informes.map(i => (
            <div key={i.clave} className="rounded-lg border border-linea bg-white p-5">
              <h2 className="font-medium">{i.nombre}</h2>
              <p className="mt-1 text-sm text-apagado">{i.descripcion}</p>
              {i.faltante && (
                <p className="mt-2 rounded-md border border-dorado/50 bg-dorado-claro px-3 py-2 text-xs">
                  ⚠ {i.faltante}
                </p>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  className={boton}
                  disabled={!!i.faltante || enCurso !== null}
                  onClick={() => void generar(i.clave)}
                >
                  {enCurso === i.clave ? tr('Generando…', 'Generating…') : tr('Generar ahora', 'Generate now')}
                </button>
                <label className="flex items-center gap-1.5 text-xs text-apagado">
                  <input
                    type="checkbox"
                    checked={conHistorial[i.clave] === true}
                    onChange={e => setConHistorial(v => ({...v, [i.clave]: e.target.checked}))}
                  />
                  {tr('Incluir historial 2020-2023', 'Include 2020-2023 history')}
                  <NotaDeAyuda texto={A.conHistorial} dentroDeEtiqueta />
                </label>
                {i.ultimaEjecucion && (
                  <span className="text-xs text-apagado">
                    {tr('Última vez:', 'Last time:')} {fechaHora(i.ultimaEjecucion.ejecutadoEl)}
                    {i.ultimaEjecucion.ejecutadoPor
                      ? tr(` por ${i.ultimaEjecucion.ejecutadoPor}`, ` by ${i.ultimaEjecucion.ejecutadoPor}`)
                      : ''}{' '}
                    ·{' '}
                    <a
                      href={rutaDeInformeGenerado(i.ultimaEjecucion.id)}
                      className="text-verde hover:underline"
                      download
                    >
                      ↓ {tr('descargar', 'download')}
                    </a>
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {informes.data && (
        <div className="rounded-lg border border-linea bg-white p-4">
          <h2 className="font-medium">{tr('Historial', 'History')}</h2>
          {informes.data.ejecuciones.length === 0 ? (
            <p className="mt-2 text-sm text-apagado">
              {tr('Todavía no se ha generado ningún informe.', 'No reports have been generated yet.')}
            </p>
          ) : (
            <table className="mt-2 w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-apagado">
                  <th className="py-1 font-semibold">
                    {tr('Informe', 'Report')}
                    <NotaDeAyuda texto={A.informe} />
                  </th>
                  <th className="py-1 font-semibold">
                    {tr('Generado', 'Generated')}
                    <NotaDeAyuda texto={A.generado} />
                  </th>
                  <th className="py-1 font-semibold">
                    {tr('Por', 'By')}
                    <NotaDeAyuda texto={A.por} />
                  </th>
                  <th className="py-1 text-right font-semibold">
                    {tr('Filas', 'Rows')}
                    <NotaDeAyuda texto={A.filas} />
                  </th>
                  <th className="py-1 text-right font-semibold">
                    {tr('Tardó', 'Took')}
                    <NotaDeAyuda texto={A.tardo} />
                  </th>
                  <th className="py-1 font-semibold">
                    {tr('Archivo', 'File')}
                    <NotaDeAyuda texto={A.archivo} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {informes.data.ejecuciones.map(e => (
                  <tr key={e.id} className={`border-t border-linea/70 ${e.id === ultimo ? 'bg-verde-claro/60' : ''}`}>
                    <td className="py-1">{e.informeNombre}</td>
                    <td className="py-1">{fechaHora(e.ejecutadoEl)}</td>
                    <td className="py-1">{e.ejecutadoPor ?? '—'}</td>
                    <td className="py-1 text-right tabular-nums">{numero(e.filas)}</td>
                    <td className="py-1 text-right tabular-nums">{numero(e.duracionMs / 1000, 1)} s</td>
                    <td className="py-1">
                      <a href={rutaDeInformeGenerado(e.id)} className="text-verde hover:underline" download>
                        ↓ {e.nombreArchivo}
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
      {informes.isPending && <p className="text-apagado">{tr('Consultando…', 'Loading…')}</p>}
      <ProvisionesDeConcentrados />
    </section>
  );
}

// Todas las provisiones de Mona Minas y el consolidado historico de las aprobadas (decision 0099).
function ProvisionesDeConcentrados() {
  const provisiones = useProvisiones();
  return (
    <div className="rounded-lg border border-linea bg-white p-4" aria-label={tr('Provisiones', 'Provisions')}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-medium">{tr('Provisiones de concentrados', 'Concentrate provisions')}</h2>
        <a href={RUTA_DEL_CONSOLIDADO_DE_PROVISIONES} className="text-sm text-verde hover:underline" download>
          ↓ {tr('Consolidado histórico (solo las aprobadas)', 'Historical consolidated (approved only)')}
        </a>
      </div>
      <p className="mt-1 text-sm text-apagado">
        {tr(
          'Se generan en el paquete documental de cada exportación de Mona Minas: en EXW con la instrucción de facturación, en CIF o FOB con las facturas en PDF. Una provisión sin alertas se aprueba y entra al consolidado; retirarla la saca sin borrar su archivo.',
          'They are generated in the document package of each Mona Minas export: in EXW with the invoicing instruction, in CIF or FOB with the invoices as PDF. A provision without alerts is approved and enters the consolidated; withdrawing it takes it out without deleting its file.',
        )}
      </p>
      {provisiones.isError && <p className="mt-2 text-sm text-alerta">{provisiones.error.message}</p>}
      {provisiones.data &&
        (provisiones.data.provisiones.length ? (
          <TablaDeProvisiones provisiones={provisiones.data.provisiones} conCodigo />
        ) : (
          <p className="mt-2 text-sm text-apagado">
            {tr('Todavía no se ha generado ninguna provisión.', 'No provisions have been generated yet.')}
          </p>
        ))}
    </div>
  );
}
